<template>
  <l-default :class="b()">
    <c-container>
      <slot name="breadcrumb"></slot>
      <slot
        name="header"
        :config="headerConfig"
      ></slot>

      <c-filter-state
        v-if="$viewport.isMd"
        :class="b('filter-state')"
        :filter-groups="filterGroups"
        @update-filter="onFilterChange"
      />
      <div :class="b('container')">
        <e-loading-indicator
          v-if="loading"
          :class="b('loader')"
          size="medium"
          fullwidth
        />

        <aside :class="b('filter')">
          <slot
            name="filters"
            :filter-groups="filterGroups"
            @update-filter="onFilterChange"
          ></slot>
        </aside>
        <main :class="b('list-container')">
          <slot name="top-element"></slot>
          <div :class="b('list')">
            <slot
              name="items"
              :items="items"
              :loading="loading"
            ></slot>
            <div
              v-if="!items.length && !loading"
              :class="b('no-items-found')"
            >
              <p>{{ noItemsFoundMessage }}</p>
              <e-button
                v-if="filterGroupsWithSelection.length"
                type="button"
                variant="text-underline"
                @click="onResetFilter"
              >
                {{ $t('c-filter-state.button-reset-all-filters') }}
              </e-button>
            </div>
          </div>

          <c-pagination
            v-if="items.length && pagination?.moreThanOnePage()"
            :class="b('pagination')"
            :pagination="pagination"
            show-advanced-controls
            @change="onPageChange"
          />
        </main>
      </div>
    </c-container>
  </l-default>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import cContainer from '@/components/c-container.vue';
  import cFilterState from '@/components/c-filter-state.vue';
  import cPagination from '@/components/c-pagination.vue';
  import eButton from '@/elements/e-button.vue';
  import eLoadingIndicator from '@/elements/e-loading-indicator.vue';
  import { Pagination } from '@/helpers/pagination';
  import lDefault from '@/layouts/l-default.vue';
  import { FilterGroup } from '@/types/filter';
  import { PageHeaderConfig } from './c-page-header.vue';

  export default defineComponent({
    name: 'c-list-page',

    components: {
      lDefault,
      cContainer,
      cFilterState,
      cPagination,
      eButton,
      eLoadingIndicator,
    },

    props: {
      /**
       * The filter groups to display filter state for
       */
      filterGroups: {
        type: Array as PropType<FilterGroup[]>,
        required: true,
      },

      /**
       * The pagination object
       */
      pagination: {
        type: Object as PropType<Pagination>,
        required: true,
      },

      /**
       * The items to display the listing for
       */
      items: {
        type: Array as PropType<Record<string, unknown>[]>,
        required: true,
      },

      /**
       * Indicating loading state
       */
      loading: {
        type: Boolean,
        default: false,
      },

      /**
       * The header config
       */
      headerConfig: {
        type: Object as PropType<PageHeaderConfig>,
        required: true,
      },

      /**
       * The message to show if no items where found
       */
      noItemsFoundMessage: {
        type: String,
        required: true,
      },
    },

    emits: {
      updateFilter: () => true, // No payload expected
      resetFilter: (payload: { clearAllFilters: boolean }) => typeof payload.clearAllFilters === 'boolean',
      pageChange: (page: number) => typeof page === 'number' && page > 0,
    },

    computed: {
      filterGroupsWithSelection(): FilterGroup[] {
        return this.filterGroups.filter(
          (group) => group.values.length > 0 && group.values.some((value) => value.selected === true),
        );
      },
    },

    methods: {
      onFilterChange() {
        this.$emit('updateFilter');
      },

      onResetFilter() {
        this.$emit('resetFilter', { clearAllFilters: true });
      },

      onPageChange(page: number) {
        this.$emit('pageChange', page);
      },
    },
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/mixins';

  .c-list-page {
    --cls-min-height: initial;

    padding: 3rem 0;

    @include mixins.media(md) {
      --cls-min-height: 45vh;
    }

    @include mixins.media(lg) {
      --cls-min-height: 55vh;
    }

    @include mixins.media(xl) {
      --cls-min-height: 60vh;
    }

    &__filter-state {
      margin-bottom: 2.5rem;
    }

    &__container {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      @include mixins.media(md) {
        flex-direction: row;
      }
    }

    &__filter {
      background-color: var(--theme-color-grayscale--950);
      padding: 1.5rem;
      width: 100%;
      min-height: var(--cls-min-height);

      @include mixins.media(md) {
        width: 25%;
      }
    }

    &__list-container {
      position: relative;
      width: 100%;
      min-height: var(--cls-min-height);

      @include mixins.media(md) {
        width: 75%;
      }
    }

    &__list {
      $list-gap-size: 1.5rem;

      display: grid;
      grid-template-columns: 1fr;
      gap: $list-gap-size;
      align-items: start;

      @include mixins.media(md) {
        grid-template-columns: repeat(2, calc(50% - $list-gap-size));
      }

      @include mixins.media(lg) {
        grid-template-columns: repeat(3, calc(33% - ($list-gap-size / 2)));
      }
    }

    &__pagination {
      margin-top: 4rem;
    }

    &__no-items-found {
      font-style: italic;
      padding: 1rem;
    }
  }
</style>
